import { useState, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import { supabase } from "@/integrations/supabase/client";
import { useMultilingualContent } from '@/hooks/useMultilingualContent';

const NEWSLETTER_KEYS = [
  "newsletter_title",
  "newsletter_title_accent",
  "newsletter_description",
  "newsletter_feature_1",
  "newsletter_feature_2",
  "newsletter_feature_3",
  "newsletter_input_placeholder",
  "newsletter_button_text",
  "newsletter_subscribers_count",
  "newsletter_footer_text",
] as const;

const DEFAULT_CONTENT = {
  newsletter_title: "Junte-se ao Meu",
  newsletter_title_accent: "Círculo Íntimo",
  newsletter_description: "Receba conteúdo exclusivo, acesso antecipado a novos vídeos e surpresas especiais direto na sua caixa de entrada toda semana.",
  newsletter_feature_1: "Dicas Semanais",
  newsletter_feature_2: "Conteúdo Exclusivo",
  newsletter_feature_3: "Acesso Antecipado",
  newsletter_input_placeholder: "Digite seu email",
  newsletter_button_text: "Inscrever-se",
  newsletter_subscribers_count: "15.000+",
  newsletter_footer_text: "inscritos. Cancele a qualquer momento.",
};

type NewsletterContent = typeof DEFAULT_CONTENT;

export const useNewsletterContent = () => {
  const { i18n } = useTranslation();
  const { transformContent } = useMultilingualContent();
  const [content, setContent] = useState<NewsletterContent>(DEFAULT_CONTENT);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const fetchContent = async () => {
      const { data } = await supabase
        .from("site_content")
        .select("content_key, content_value, content_value_en, content_value_es")
        .in("content_key", NEWSLETTER_KEYS);

      if (data) {
        const transformedData = transformContent(data);
        const contentMap = { ...DEFAULT_CONTENT };
        
        // transformContent returns Record<string, string> already
        NEWSLETTER_KEYS.forEach((key) => {
          if (transformedData[key] && transformedData[key].trim() !== '') {
            contentMap[key] = transformedData[key];
          }
        });
        
        setContent(contentMap);
        setIsLoaded(true);
      }
    };

    fetchContent();

    // Set up real-time subscription to site_content table
    const channel = supabase
      .channel(`newsletter-content-changes-${Math.random().toString(36).slice(2)}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "site_content",
          filter: `content_key=in.(${NEWSLETTER_KEYS.join(",")})`,
        },
        (payload) => {
          // Re-fetch content when any newsletter field changes
          fetchContent();
        }
      )
      .subscribe();

    // Cleanup subscription on unmount
    return () => {
      supabase.removeChannel(channel);
    };
  }, [i18n.language]);

  return { content, isLoaded };
};
